import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';

/**
 * ONT que el cliente YA tiene instalada, para poder elegirla en "ONT a
 * Utilizar" durante la activación de un traslado.
 *
 * El reparto de responsabilidades es a propósito: Django clasifica la serie y
 * devuelve los payloads ya armados (qué bodega, qué ubicación, qué producto), y
 * acá solo se despachan contra las APIs de inventario que ya existen y están
 * auditadas. Así ninguna regla de inventario queda escrita en el front.
 */

const { post } = erpApi();

/** Caso en que está la serie dentro del inventario de EVA. */
export type CasoOntCliente =
  | 'A' // ya está a nombre del cliente: no hay nada que escribir
  | 'B' // existe sin ubicación (egresada): necesita reingreso antes del ingreso
  | 'C' // no existe en EVA: se la ingresa
  | 'D'; // existe en otra bodega: se usa igual, avisando

export type AccionOntCliente =
  | 'crear_ubicacion'
  | 'marcar_reingreso'
  | 'ingreso_inventario';

/** Opción con la forma que consume el combo "ONT a Utilizar". */
export type OntParaFormulario = {
  name: string;
  codigo: string;
  cantidad: string;
  series: string[];
};

export type UbicacionOntCliente = {
  codigo: string | null;
  nombre: string | null;
  bodega_codigo?: string | null;
  bodega_nombre?: string | null;
  es_cliente?: boolean;
  existe?: boolean;
};

export type ConsultaOntCliente = {
  serie: string;
  serie_recibida: string;
  serie_en_inventario: string | null;
  modelo: string | null;
  caso: CasoOntCliente;
  caso_detalle: string;
  identificacion: string;
  contrato_softnet: string | null;
  puede_usarse: boolean;
  requiere_confirmacion: boolean;
  producto: {
    codigo: string;
    descripcion: string;
    es_ont: boolean;
    contiene_serie: boolean;
    categoria: string | null;
  } | null;
  ubicacion_actual: UbicacionOntCliente | null;
  ubicacion_cliente: UbicacionOntCliente;
  acciones: AccionOntCliente[];
  payload_ubicacion: Record<string, unknown> | null;
  payload_reingreso: Record<string, unknown>[] | null;
  payload_ingreso: Record<string, unknown> | null;
  advertencias: string[];
  ont: OntParaFormulario | null;
};

/**
 * Pregunta a Django en qué estado está la serie de la ONT del cliente. No
 * escribe nada: solo clasifica y devuelve los payloads de lo que faltaría.
 */
export async function consultarOntCliente(
  idOrdenTrabajo: string,
  body: { serie: string; modelo?: string | null; contrato_softnet?: string | null },
): Promise<ConsultaOntCliente> {
  const res = await post<ConsultaOntCliente>(
    `${urlBackend}api/v1/orden-trabajo/${idOrdenTrabajo}/ont-cliente/`,
    body as any,
    true,
  );
  return res.data;
}

/**
 * Ejecuta lo que la consulta dijo que falta, en orden. Se usan los endpoints
 * oficiales de inventario con los payloads tal como los armó el backend, por
 * eso se llaman directo y no a través de las acciones del módulo de inventario
 * (sus tipos no contemplan el `codigo` fijado por el servidor).
 *
 * El orden importa:
 *  1. reingreso: libera la serie histórica (la renombra a `<serie>_REINGRESO`),
 *     porque si no el ingreso la rechaza con "La serie ya existe".
 *  2. ubicación: crea `Cliente <identificación>` si todavía no existe.
 *  3. ingreso: registra la serie en la bodega de clientes.
 */
export async function registrarOntCliente(
  consulta: ConsultaOntCliente,
): Promise<OntParaFormulario> {
  if (!consulta.ont) {
    throw new Error('La consulta no devolvió una ONT utilizable.');
  }

  if (consulta.acciones.includes('marcar_reingreso') && consulta.payload_reingreso) {
    const res = await post<unknown>(
      `${urlBackend}api/v1/ingreso-inventario/marcar-reingreso/`,
      consulta.payload_reingreso as any,
      true,
    );
    // Este endpoint responde 200 incluso cuando no actualizó nada, así que se
    // revisa el cuerpo y no solo el código HTTP.
    if (res.status === 'error') {
      throw new Error(res.message || 'No se pudo marcar la serie como reingreso.');
    }
  }

  if (consulta.acciones.includes('crear_ubicacion') && consulta.payload_ubicacion) {
    const res = await post<unknown>(
      `${urlBackend}api/v1/ubicacion/`,
      consulta.payload_ubicacion as any,
      true,
    );
    if (res.status === 'error') {
      throw new Error(res.message || 'No se pudo crear la ubicación del cliente.');
    }
  }

  if (consulta.acciones.includes('ingreso_inventario') && consulta.payload_ingreso) {
    const res = await post<unknown>(
      `${urlBackend}api/v1/ingreso-inventario/`,
      consulta.payload_ingreso as any,
      true,
    );
    if (res.status === 'error') {
      throw new Error(res.message || 'No se pudo ingresar la serie al inventario.');
    }
  }

  return consulta.ont;
}

/** Texto corto del caso, para el chip de la lista. */
export const ROTULO_CASO: Record<CasoOntCliente, string> = {
  A: 'Registrada al cliente',
  B: 'Necesita reingreso',
  C: 'Falta registrarla',
  D: 'En otra bodega',
};
